import { CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HealthStatus } from '@/api/types';

interface HealthPillProps {
  status: HealthStatus | 'loading';
  label?: string;
  className?: string;
}

/**
 * HealthPill — global health indicator for the top bar. Uses icon + color
 * + text per §8.9 (never color alone). Amber (not orange) for warning.
 */
export function HealthPill({ status, label, className }: HealthPillProps) {
  const { Icon, color, defaultLabel } = STATUS_MAP[status] ?? STATUS_MAP.loading;
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium',
        color,
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      <span>{label ?? defaultLabel}</span>
    </div>
  );
}

const STATUS_MAP: Record<
  HealthPillProps['status'],
  { Icon: typeof CheckCircle2; color: string; defaultLabel: string }
> = {
  ok: {
    Icon: CheckCircle2,
    color: 'bg-success/10 text-success border-success/30',
    defaultLabel: 'All systems normal',
  },
  degraded: {
    Icon: AlertTriangle,
    color: 'bg-warning/10 text-warning border-warning/30',
    defaultLabel: 'Degraded',
  },
  down: {
    Icon: XCircle,
    color: 'bg-destructive/10 text-destructive border-destructive/30',
    defaultLabel: 'System down',
  },
  loading: {
    Icon: Loader2,
    color: 'bg-muted text-muted-foreground',
    defaultLabel: 'Checking…',
  },
};
